/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Button, Grid, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import Section from 'bundles/front-page/components/Section';
import OverflowCarousel from 'bundles/front-page/components/overflow-carousel/OverflowCarousel';
import useLohpCollection from 'bundles/front-page/hooks/useLohpCollection';
import CompactLearningProductCard from 'bundles/front-page/redesign-modules/spotlight-collections/CompactLearningProductCard';

import _t from 'i18n!nls/front-page';

const MAX_PRODUCT_CARDS = 4;

type Props = {
  headline: string;
  description: string;
  ctaText: string;
  ctaTarget: string;
  collectionSets: {
    id: string;
    collectionModuleId: string;
    presetCollectionId?: string;
    filterName?: string;
  }[];
};

const styles = {
  container: css`
    padding: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-300);
    display: grid;
    grid-template-columns: 1fr 4fr;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('md')} {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      gap: var(--cds-spacing-200);
    }
  `,
  containerWithBackground: css`
    margin: var(--cds-spacing-200) 0;
    padding: var(--cds-spacing-200) 0;
  `,
  sectionBackground: (isRTL: boolean) => css`
    ${isRTL
      ? `background: linear-gradient(
      86deg,
      var(--cds-color-green-300) 3.56%,
      var(--cds-color-emphasis-primary-background-default) 40.71%,
      var(--cds-color-emphasis-primary-background-strong) 96.44%
    );`
      : `background: linear-gradient(
      86deg,
      var(--cds-color-emphasis-primary-background-strong) 3.56%,
      var(--cds-color-emphasis-primary-background-default) 40.71%,
      var(--cds-color-green-300) 96.44%
    )`}
  `,
  sectionTitle: css`
    ${breakpoints.down('xs')} {
      ${typography2.subtitleMedium}
    }
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    ${breakpoints.down('md')} {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  `,
  description: css`
    margin-top: var(--cds-spacing-50);
  `,
  cta: css`
    margin-top: var(--cds-spacing-150);
  `,
  ctaIconButton: css`
    padding: 0;

    ${breakpoints.down('md')} {
      &:hover {
        box-shadow: 0 0 0 1px var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  collection: css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      /* 600 / 4 = 150 */
      grid-template-columns: repeat(4, minmax(150px, 1fr));
    }

    /* !important to override the styles set by CDS */
    .cds-ProductCard-grid {
      min-width: unset !important;
      border-radius: var(--cds-border-radius-100) !important;
    }

    .cds-ProductCard-content {
      padding: 0 !important;
    }

    .cds-Avatar-base {
      width: 12px !important;
      height: 12px !important;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    ${breakpoints.down('xs')} {
      .cds-CommonCard-title {
        ${typography2.subtitleSmall}
      }

      .cds-CommonCard-metadata,
      .cds-CommonCard-metadata p,
      .cds-ProductCard-partnerNames {
        ${typography2.bodyTertiary}
      }

      .cds-ProductCard-gridPreviewContainer .cds-CommonCard-previewImage {
        margin: calc(var(--cds-spacing-100) * -1) calc(var(--cds-spacing-100) * -1) var(--cds-spacing-100)
          calc(var(--cds-spacing-100) * -1) !important;
        border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0 !important;
      }
    }
  `,
};

const SpotlightCollectionsModuleRenderer = ({ headline, description, ctaText, ctaTarget, collectionSets }: Props) => {
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('md'));
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const currentCollectionSetIndex = 0;
  const collectionIndex = currentCollectionSetIndex;
  const isRTL = isRightToLeft(_t.getLocale());

  const { collection, loading: isCollectionsLoading } = useLohpCollection({
    queryCollectionId: collectionSets[currentCollectionSetIndex].collectionModuleId,
    additionalInput: {
      preset_collection_id: collectionSets[currentCollectionSetIndex].presetCollectionId,
    },
  });

  if (isCollectionsLoading) {
    // TODO: Add loading state in the next PR
    return <Section>Loading...</Section>;
  }

  const output = (
    <>
      <div css={styles.contentContainer}>
        <Typography2 variant="subtitleLarge" component="h2" color="invertBody" css={styles.sectionTitle}>
          {headline}
        </Typography2>
        {description && (
          <Typography2 variant="bodyPrimary" color="invertBody" component="p" css={styles.description}>
            {description}
          </Typography2>
        )}
        {ctaText &&
          (isTabletOrSmaller ? (
            <Button
              variant="ghostInvert"
              component="a"
              href={ctaTarget}
              css={styles.ctaIconButton}
              aria-label={ctaText}
            >
              <ArrowNextIcon color="invert" size="large" />
            </Button>
          ) : (
            <Button variant="secondary" size="small" component="a" href={ctaTarget} css={styles.cta}>
              {ctaText}
            </Button>
          ))}
      </div>
      <OverflowCarousel css={styles.collection} shouldAllowSpaceForHoverItems>
        {collection?.entities
          .filter((entity) => !!entity.name)
          .slice(0, MAX_PRODUCT_CARDS)
          .map((entity, index) => (
            <CompactLearningProductCard
              entity={entity}
              entityIndex={index}
              collectionIndex={collectionIndex}
              collection={collection}
              key={entity.id}
            />
          ))}
      </OverflowCarousel>
    </>
  );

  if (isMobile) {
    return <Section css={[styles.sectionBackground(isRTL), styles.containerWithBackground]}>{output}</Section>;
  }

  return (
    <Section>
      <Grid item xs={12} css={[styles.container, styles.sectionBackground(isRTL)]}>
        {output}
      </Grid>
    </Section>
  );
};

export default SpotlightCollectionsModuleRenderer;
